import { LineSeriesOption } from 'echarts';
import { useCallback, useEffect, useState } from 'react';
import { ChartApi, CursorCoordinates, usePanelTooltipActions } from '@/components';

interface UseTimeChartPinningProps {
  chartApiRef: React.MutableRefObject<ChartApi | undefined>;
}

interface UseTimeChartPinningResult {
  tooltipPinnedCoords: CursorCoordinates | null;
  setTooltipPinnedCoords: React.Dispatch<React.SetStateAction<CursorCoordinates | null>>;
  pinnedCrosshair: LineSeriesOption | null;
  setPinnedCrosshair: React.Dispatch<React.SetStateAction<LineSeriesOption | null>>;
  clearPinned: () => void;
}

export function useTimeChartPinning({ chartApiRef }: UseTimeChartPinningProps): UseTimeChartPinningResult {
  const [tooltipPinnedCoords, setTooltipPinnedCoords] = useState<CursorCoordinates | null>(null);
  const [pinnedCrosshair, setPinnedCrosshair] = useState<LineSeriesOption | null>(null);

  const { handleUnpin } = usePanelTooltipActions();

  const clearPinned = useCallback(() => {
    setTooltipPinnedCoords(null);
    setPinnedCrosshair(null);
    handleUnpin(); // Unpin tooltip for other chart types that use PanelTooltipProvider
  }, [handleUnpin]);

  // Unpin tooltip when clicking outside the chart
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      // Return if user is attempting to pin multiple tooltips
      const isControlKeyPressed = e.ctrlKey || e.metaKey;
      if (isControlKeyPressed) {
        return;
      }

      // Check if the event target is current chart. If not, unpin tooltip
      const chartTarget = chartApiRef.current?.getContainerEl();
      if (chartTarget && e.target && !chartTarget.contains(e.target as Node)) {
        clearPinned();
      }
    }

    window.addEventListener('click', handleClick);

    return () => {
      window.removeEventListener('click', handleClick);
    };
  }, [chartApiRef, clearPinned]);

  return {
    clearPinned,
    tooltipPinnedCoords,
    setTooltipPinnedCoords,
    pinnedCrosshair,
    setPinnedCrosshair,
  };
}
